import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Message } from "semantic-ui-react";

import {
  buildPurDraftCreatePayload, canUsePurDraftCreatePilot, checkPurDraftCreatePermissions, createPurDraft,
  PurDraftCreateAttempt, PurDraftCreateError, PurDraftCreatePermission, purDraftPermissionNames, reconcilePurDraftCreate,
} from "react-lib/apis/PUR/purDraftCreate";
import { PurRecord } from "react-lib/apis/PUR/purApi";
import { PurClientConfig, readId } from "react-lib/apis/PUR/purRead";

import { DraftIssue, PurchaseDraft, purchaseCreateInput, validatePurchaseCreate } from "./purDrafts";
import { formatMeerpMessage, formatPurTimestamp } from "./purPresentation";

type Phase = "idle" | "checking" | "confirming" | "saving" | "saved" | "unknown" | "reconciling";
interface SaveState {
  phase: Phase; permission: PurDraftCreatePermission | null; issues: DraftIssue[]; errorId: string | null;
  attempt: PurDraftCreateAttempt | null; record: PurRecord | null; recordId: number | null;
}
const initialState = (): SaveState => ({ phase: "idle", permission: null, issues: [], errorId: null, attempt: null, record: null, recordId: null });

// Call this in the always-mounted workspace, not in a tab's form. A navigation
// away and back must not clear an in-flight/unknown/successful create operation.
export const usePurDraftSave = (client: PurClientConfig, value: PurchaseDraft) => {
  const [state, setState] = useState<SaveState>(initialState);
  const active = useRef(true);
  const busy = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const locked = ["checking", "confirming", "saving", "saved", "unknown", "reconciling"].includes(state.phase);
  const canReset = !["checking", "confirming", "saving", "unknown", "reconciling"].includes(state.phase);
  useEffect(() => {
    active.current = true;
    const warn = (event: BeforeUnloadEvent) => {
      if (["saving", "unknown", "reconciling"].includes(stateRef.current.phase)) {
        event.preventDefault(); event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => { active.current = false; window.removeEventListener("beforeunload", warn); };
  }, []);
  const check = async () => {
    if (busy.current || !canUsePurDraftCreatePilot(client) || !canReset || state.phase === "saved") return;
    busy.current = true; setState((previous) => ({ ...previous, phase: "checking", errorId: null, permission: null }));
    try {
      const permission = (await checkPurDraftCreatePermissions(client))[value.kind];
      if (active.current) setState((previous) => ({ ...previous, phase: "idle", permission }));
    } catch {
      if (active.current) setState((previous) => ({ ...previous, phase: "idle", errorId: "MEERP_CREATE_CHECK_ERROR" }));
    } finally { busy.current = false; }
  };
  const prepare = () => {
    if (busy.current || locked || !state.permission?.resource || !state.permission.action) return;
    const issues = validatePurchaseCreate(value);
    if (issues.length) { setState((previous) => ({ ...previous, issues, errorId: null })); return; }
    try {
      const attempt = { input: { ...purchaseCreateInput(value), requestingDivisionId: readId(client.divisionId) ?? undefined }, marker: `MEERP-CREATE-${Date.now()}-${Math.random().toString(36).slice(2, 12)}` };
      buildPurDraftCreatePayload(attempt);
      setState((previous) => ({ ...previous, phase: "confirming", attempt, issues: [], errorId: null }));
    } catch (error: unknown) {
      setState((previous) => ({ ...previous, errorId: error instanceof PurDraftCreateError ? error.messageId : "MEERP_CREATE_VALIDATION_ERROR" }));
    }
  };
  const confirm = async () => {
    if (busy.current || state.phase !== "confirming" || !state.attempt) return;
    busy.current = true;
    const attempt = state.attempt;
    setState((previous) => ({ ...previous, phase: "saving" }));
    try {
      const record = await createPurDraft(client, attempt, () => active.current);
      if (active.current) setState((previous) => ({ ...previous, phase: "saved", record, recordId: record.id, errorId: null }));
    } catch (error: unknown) {
      const failure = error instanceof PurDraftCreateError ? error : new PurDraftCreateError("blocked", "MEERP_CREATE_PREFLIGHT_ERROR");
      if (active.current) setState((previous) => ({ ...previous, phase: failure.outcome === "unknown" ? "unknown" : "idle",
        errorId: failure.messageId, recordId: failure.recordId, permission: failure.outcome === "unknown" ? previous.permission : null }));
    } finally { busy.current = false; }
  };
  const reconcile = async (idText: string) => {
    const id = readId(idText);
    if (busy.current || state.phase !== "unknown" || !state.attempt || id === null) return;
    busy.current = true;
    setState((previous) => ({ ...previous, phase: "reconciling" }));
    try {
      const record = await reconcilePurDraftCreate(client, state.attempt, id);
      if (active.current) setState((previous) => ({ ...previous, phase: "saved", record, recordId: record.id, errorId: null }));
    } catch {
      if (active.current) setState((previous) => ({ ...previous, phase: "unknown", errorId: "MEERP_CREATE_READBACK_ERROR" }));
    } finally { busy.current = false; }
  };
  return { state, locked, canReset, check, prepare, confirm, reconcile,
    cancel: () => { if (!busy.current && state.phase === "confirming") setState((previous) => ({ ...previous, phase: "idle", attempt: null })); },
    reset: () => { if (!busy.current && canReset) setState(initialState()); } };
};

export type PurDraftSaveController = ReturnType<typeof usePurDraftSave>;
const PurDraftSave: React.FC<{ client: PurClientConfig; value: PurchaseDraft; controller: PurDraftSaveController; onOpenSaved?: (record: PurRecord) => void }> = ({ client, value, controller, onOpenSaved }) => {
  const intl = useIntl();
  const t = (id: string) => formatMeerpMessage(intl, id);
  const [idText, setIdText] = useState("");
  const { state } = controller;
  const names = purDraftPermissionNames(value.kind);
  const eligible = canUsePurDraftCreatePilot(client);
  const permission = state.permission;
  const unknown = state.phase === "unknown" || state.phase === "reconciling";
  return <section className="card-meerp__draft-save" aria-label={t("MEERP_CREATE_SECTION")}>
    <Message info><p>{t("MEERP_CREATE_PILOT_NOTE")}</p><p>{t("MEERP_CREATE_TEXT_MAPPING")}</p></Message>
    {!eligible && <Message warning>{t("MEERP_CREATE_CONTEXT_ERROR")}</Message>}
    <Button type="button" disabled={!eligible || controller.locked} loading={state.phase === "checking"} onClick={controller.check}>{t("MEERP_CREATE_CHECK")}</Button>
    {permission && <Message positive={permission.resource && permission.action} warning={!permission.resource || !permission.action} role="status">
      {permission.resource && permission.action ? t("MEERP_CREATE_PERMISSION_READY") : <>
        <p>{t("MEERP_CREATE_PERMISSION_MISSING")}</p>
        <ul>{!permission.resource && <li><code>{names.resource}</code></li>}{!permission.action && <li><code>{names.action}</code></li>}</ul>
      </>}
    </Message>}
    {state.issues.length > 0 && <Message negative role="alert"><ul>{state.issues.map((issue, index) => <li key={index}>{issue.line
      ? intl.formatMessage({ id: "MEERP_DRAFT_LINE_ISSUE" }, { line: issue.line, message: t(issue.id) }) : t(issue.id)}</li>)}</ul></Message>}
    {state.errorId && <Message negative role="alert">{t(state.errorId)}</Message>}
    <Button type="button" primary disabled={!eligible || controller.locked || !permission?.resource || !permission.action} onClick={controller.prepare}>{t("MEERP_ACTION_SAVE_DRAFT")}</Button>
    {state.phase === "confirming" && <Message warning role="alert">
      <p>{intl.formatMessage({ id: "MEERP_CREATE_CONFIRM_NOTE" }, { name: t(value.kind === "materialRequests" ? "MEERP_MATERIAL_REQUESTS" : "MEERP_PURCHASE_ORDERS"), count: value.lines.length })}</p>
      <code>{state.attempt?.marker}</code><br />
      <Button type="button" onClick={controller.confirm}>{t("MEERP_CREATE_CONFIRM")}</Button>
      <Button type="button" onClick={controller.cancel}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button>
    </Message>}
    {state.phase === "saving" && <Message info role="status">{t("MEERP_CREATE_SAVING")}</Message>}
    {state.phase === "saved" && state.record && <Message positive role="status">
      {intl.formatMessage({ id: "MEERP_CREATE_SAVED" }, { number: state.record.number, id: state.record.id })}
      <p>{t("MEERP_CREATE_SAVED_LOCK")}</p>
      <p>{t("MEERP_CREATED_AT")}: {formatPurTimestamp(intl, state.record.createdAt)}</p>
      {onOpenSaved && <Button type="button" onClick={() => state.record && onOpenSaved(state.record)}>{t("MEERP_OPEN_SAVED")}</Button>}
    </Message>}
    {unknown && <Message warning>
      <p>{t("MEERP_CREATE_RECONCILE_NOTE")}</p><code>{state.attempt?.marker}</code>
      <label htmlFor="pur-create-reconcile-id">{t("MEERP_CREATE_RECONCILE_ID")}</label>
      <input id="pur-create-reconcile-id" inputMode="numeric" value={state.recordId === null ? idText : String(state.recordId)} disabled={state.recordId !== null || state.phase === "reconciling"} onChange={(event) => setIdText(event.target.value)} />
      <Button type="button" disabled={state.phase === "reconciling" || readId(state.recordId ?? idText) === null} loading={state.phase === "reconciling"} onClick={() => controller.reconcile(String(state.recordId ?? idText))}>{t("MEERP_CREATE_RECONCILE")}</Button>
    </Message>}
    <p className="card-meerp__muted">{t("MEERP_CREATE_LIMITS")}</p>
  </section>;
};
export default PurDraftSave;
